import { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polygon, ZoomControl, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './StarkRwaeMarketplace.css';
import { useTranslation } from 'react-i18next';


const TOKEN_SOURCES = {
  RENT_ASSET_PROFILE: { prefix: 'RI', label: 'Rental Income', group: 'Income' },
  REVSHARE_ASSET_PROFILE: { prefix: 'RS', label: 'Revenue Share', group: 'Income' },
  SALEPROFIT_ASSET_PROFILE: { prefix: 'SP', label: 'Sale Profit', group: 'Exit' },
  EQAPP_ASSET_PROFILE: { prefix: 'EA', label: 'Equity Appreciation', group: 'Equity' },
  PREFEQ_ASSET_PROFILE: { prefix: 'PE', label: 'Preferred Equity', group: 'Equity' },
  COMEQ_ASSET_PROFILE: { prefix: 'CE', label: 'Common Equity', group: 'Equity' },
  DEBT_ASSET_PROFILE: { prefix: 'DN', label: 'Debt Note', group: 'Debt' },
  HYBRID_ASSET_PROFILE: { prefix: 'HY', label: 'Hybrid', group: 'Hybrid' },
  PREAUCT_AUCTION_OPPORTUNITY: { prefix: 'PA', label: 'Pre-Auction Pool', group: 'Auction' },
  POSTAUCT_AUCTION_RESULT_INTAKE: { prefix: 'PO', label: 'Post-Auction Token', group: 'Auction' },
};

const DEFAULT_CENTER = [39.8283, -98.5795];
const USA_BOUNDS = [[18.0, -170.0], [72.0, -55.0]];
const WORLD_BOUNDS = [[-85, -180], [85, 180]];

const BASE_COUNTRIES = [
  'United States', 'Australia', 'Austria', 'Bahrain', 'Belgium', 'Brazil', 'Canada',
  'China', 'Denmark', 'Finland', 'France', 'Germany', 'Hong Kong', 'Iceland',
  'India', 'Indonesia', 'Ireland', 'Israel', 'Italy', 'Japan', 'Kuwait',
  'Luxembourg', 'Malaysia', 'Mexico', 'Netherlands', 'New Zealand', 'Norway',
  'Portugal', 'Qatar', 'Russia', 'Saudi Arabia', 'Singapore', 'South Korea',
  'Spain', 'Sweden', 'Switzerland', 'Taiwan', 'Türkiye', 'United Arab Emirates',
  'United Kingdom', 'Vietnam'
];

const COUNTRY_OPTIONS = [
  'All',
  'United States',
  ...BASE_COUNTRIES.filter((country) => country !== 'United States').sort((a, b) => a.localeCompare(b, 'en')),
];

const COUNTRY_REGION_OPTIONS = {
  'United States': [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
    'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine',
    'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri',
    'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
    'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee',
    'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming',
    'American Samoa', 'Guam', 'Northern Mariana Islands', 'Puerto Rico',
    'U.S. Virgin Islands', 'U.S. Minor Outlying Islands',
    'Marshall Islands', 'Micronesia', 'Palau'
  ],
  'Australia': ['Australian Capital Territory', 'New South Wales', 'Northern Territory', 'Queensland', 'South Australia', 'Tasmania', 'Victoria', 'Western Australia'],
  'Austria': ['Burgenland', 'Carinthia', 'Lower Austria', 'Salzburg', 'Styria', 'Tyrol', 'Upper Austria', 'Vienna', 'Vorarlberg'],
  'Bahrain': ['Capital Governorate', 'Muharraq Governorate', 'Northern Governorate', 'Southern Governorate'],
  'Belgium': ['Antwerp', 'Brussels-Capital Region', 'East Flanders', 'Flemish Brabant', 'Hainaut', 'Liège', 'Limburg', 'Luxembourg', 'Namur', 'Walloon Brabant', 'West Flanders'],
  'Brazil': ['Acre', 'Alagoas', 'Amapá', 'Amazonas', 'Bahia', 'Ceará', 'Distrito Federal', 'Espírito Santo', 'Goiás', 'Maranhão', 'Mato Grosso', 'Mato Grosso do Sul', 'Minas Gerais', 'Pará', 'Paraíba', 'Paraná', 'Pernambuco', 'Piauí', 'Rio de Janeiro', 'Rio Grande do Norte', 'Rio Grande do Sul', 'Rondônia', 'Roraima', 'Santa Catarina', 'São Paulo', 'Sergipe', 'Tocantins'],
  'Canada': ['Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan', 'Yukon'],
  'China': ['Anhui', 'Beijing', 'Chongqing', 'Fujian', 'Gansu', 'Guangdong', 'Guangxi', 'Guizhou', 'Hainan', 'Hebei', 'Heilongjiang', 'Henan', 'Hubei', 'Hunan', 'Inner Mongolia', 'Jiangsu', 'Jiangxi', 'Jilin', 'Liaoning', 'Ningxia', 'Qinghai', 'Shaanxi', 'Shandong', 'Shanghai', 'Shanxi', 'Sichuan', 'Tianjin', 'Tibet', 'Xinjiang', 'Yunnan', 'Zhejiang'],
  'Denmark': ['Capital Region of Denmark', 'Central Denmark Region', 'North Denmark Region', 'Region Zealand', 'Region of Southern Denmark', 'Faroe Islands', 'Greenland'],
  'Finland': ['Åland', 'Central Finland', 'Central Ostrobothnia', 'Kainuu', 'Kanta-Häme', 'Kymenlaakso', 'Lapland', 'North Karelia', 'North Ostrobothnia', 'North Savo', 'Ostrobothnia', 'Päijät-Häme', 'Pirkanmaa', 'Satakunta', 'South Karelia', 'South Ostrobothnia', 'South Savo', 'Southwest Finland', 'Uusimaa'],
  'France': ['Auvergne-Rhône-Alpes', 'Bourgogne-Franche-Comté', 'Brittany', 'Centre-Val de Loire', 'Corsica', 'Grand Est', 'Guadeloupe', 'French Guiana', 'Hauts-de-France', 'Île-de-France', 'Martinique', 'Mayotte', 'Normandy', 'Nouvelle-Aquitaine', 'Occitanie', 'Pays de la Loire', 'Provence-Alpes-Côte d’Azur', 'Réunion'],
  'Germany': ['Baden-Württemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg', 'Hesse', 'Lower Saxony', 'Mecklenburg-Vorpommern', 'North Rhine-Westphalia', 'Rhineland-Palatinate', 'Saarland', 'Saxony', 'Saxony-Anhalt', 'Schleswig-Holstein', 'Thuringia'],
  'Hong Kong': ['Central and Western', 'Eastern', 'Islands', 'Kowloon City', 'Kwai Tsing', 'Kwun Tong', 'North', 'Sai Kung', 'Sha Tin', 'Sham Shui Po', 'Southern', 'Tai Po', 'Tsuen Wan', 'Tuen Mun', 'Wan Chai', 'Wong Tai Sin', 'Yau Tsim Mong', 'Yuen Long'],
  'Iceland': ['Capital Region', 'Eastern Region', 'Northeastern Region', 'Northwestern Region', 'Southern Peninsula', 'Southern Region', 'Western Region', 'Westfjords'],
  'India': ['Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chandigarh', 'Chhattisgarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir', 'Jharkhand', 'Karnataka', 'Kerala', 'Ladakh', 'Lakshadweep', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'],
  'Indonesia': ['Aceh', 'Bali', 'Banten', 'Bengkulu', 'Central Java', 'Central Kalimantan', 'Central Papua', 'Central Sulawesi', 'East Java', 'East Kalimantan', 'East Nusa Tenggara', 'Gorontalo', 'Highland Papua', 'Jakarta', 'Jambi', 'Lampung', 'Maluku', 'North Kalimantan', 'North Maluku', 'North Sulawesi', 'North Sumatra', 'Papua', 'Riau', 'Riau Islands', 'South Kalimantan', 'South Papua', 'South Sulawesi', 'South Sumatra', 'Southeast Sulawesi', 'Southwest Papua', 'West Java', 'West Kalimantan', 'West Nusa Tenggara', 'West Papua', 'West Sulawesi', 'West Sumatra', 'Yogyakarta'],
  'Ireland': ['Carlow', 'Cavan', 'Clare', 'Cork', 'Donegal', 'Dublin', 'Galway', 'Kerry', 'Kildare', 'Kilkenny', 'Laois', 'Leitrim', 'Limerick', 'Longford', 'Louth', 'Mayo', 'Meath', 'Monaghan', 'Offaly', 'Roscommon', 'Sligo', 'Tipperary', 'Waterford', 'Westmeath', 'Wexford', 'Wicklow'],
  'Israel': ['Central District', 'Haifa District', 'Jerusalem District', 'Northern District', 'Southern District', 'Tel Aviv District'],
  'Italy': ['Abruzzo', 'Aosta Valley', 'Apulia', 'Basilicata', 'Calabria', 'Campania', 'Emilia-Romagna', 'Friuli Venezia Giulia', 'Lazio', 'Liguria', 'Lombardy', 'Marche', 'Molise', 'Piedmont', 'Sardinia', 'Sicily', 'Trentino-Alto Adige', 'Tuscany', 'Umbria', 'Veneto'],
  'Japan': ['Aichi', 'Akita', 'Aomori', 'Chiba', 'Ehime', 'Fukui', 'Fukuoka', 'Fukushima', 'Gifu', 'Gunma', 'Hiroshima', 'Hokkaido', 'Hyogo', 'Ibaraki', 'Ishikawa', 'Iwate', 'Kagawa', 'Kagoshima', 'Kanagawa', 'Kochi', 'Kumamoto', 'Kyoto', 'Mie', 'Miyagi', 'Miyazaki', 'Nagano', 'Nagasaki', 'Nara', 'Niigata', 'Oita', 'Okayama', 'Okinawa', 'Osaka', 'Saga', 'Saitama', 'Shiga', 'Shimane', 'Shizuoka', 'Tochigi', 'Tokushima', 'Tokyo', 'Tottori', 'Toyama', 'Wakayama', 'Yamagata', 'Yamaguchi', 'Yamanashi'],
  'Kuwait': ['Al Ahmadi', 'Al Asimah', 'Al Farwaniyah', 'Al Jahra', 'Hawalli', 'Mubarak Al-Kabeer'],
  'Luxembourg': ['Capellen', 'Clervaux', 'Diekirch', 'Echternach', 'Esch-sur-Alzette', 'Grevenmacher', 'Luxembourg', 'Mersch', 'Redange', 'Remich', 'Vianden', 'Wiltz'],
  'Malaysia': ['Johor', 'Kedah', 'Kelantan', 'Kuala Lumpur', 'Labuan', 'Malacca', 'Negeri Sembilan', 'Pahang', 'Penang', 'Perak', 'Perlis', 'Putrajaya', 'Sabah', 'Sarawak', 'Selangor', 'Terengganu'],
  'Mexico': ['Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas', 'Chihuahua', 'Coahuila', 'Colima', 'Durango', 'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'Mexico City', 'México', 'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas'],
  'Netherlands': ['Drenthe', 'Flevoland', 'Friesland', 'Gelderland', 'Groningen', 'Limburg', 'North Brabant', 'North Holland', 'Overijssel', 'South Holland', 'Utrecht', 'Zeeland', 'Aruba', 'Curaçao', 'Sint Maarten', 'Bonaire', 'Saba', 'Sint Eustatius'],
  'New Zealand': ['Auckland', 'Bay of Plenty', 'Canterbury', 'Chatham Islands', 'Gisborne', 'Hawke’s Bay', 'Manawatū-Whanganui', 'Marlborough', 'Nelson', 'Northland', 'Otago', 'Southland', 'Taranaki', 'Tasman', 'Waikato', 'Wellington', 'West Coast'],
  'Norway': ['Agder', 'Akershus', 'Buskerud', 'Finnmark', 'Innlandet', 'Møre og Romsdal', 'Nordland', 'Oslo', 'Rogaland', 'Telemark', 'Troms', 'Trøndelag', 'Vestfold', 'Vestland', 'Østfold', 'Svalbard'],
  'Portugal': ['Aveiro', 'Azores', 'Beja', 'Braga', 'Bragança', 'Castelo Branco', 'Coimbra', 'Évora', 'Faro', 'Guarda', 'Leiria', 'Lisbon', 'Madeira', 'Portalegre', 'Porto', 'Santarém', 'Setúbal', 'Viana do Castelo', 'Vila Real', 'Viseu'],
  'Qatar': ['Al Daayen', 'Al Khor', 'Al Rayyan', 'Al Shamal', 'Al Wakrah', 'Doha', 'Madinat ash Shamal', 'Umm Salal'],
  'Russia': ['Adygea', 'Altai Krai', 'Altai Republic', 'Amur Oblast', 'Arkhangelsk Oblast', 'Astrakhan Oblast', 'Bashkortostan', 'Belgorod Oblast', 'Bryansk Oblast', 'Buryatia', 'Chechen Republic', 'Chelyabinsk Oblast', 'Chukotka Autonomous Okrug', 'Chuvashia', 'Dagestan', 'Ingushetia', 'Irkutsk Oblast', 'Ivanovo Oblast', 'Jewish Autonomous Oblast', 'Kabardino-Balkaria', 'Kaliningrad Oblast', 'Kalmykia', 'Kaluga Oblast', 'Kamchatka Krai', 'Karachay-Cherkessia', 'Karelia', 'Kemerovo Oblast', 'Khabarovsk Krai', 'Khakassia', 'Khanty-Mansi Autonomous Okrug', 'Kirov Oblast', 'Komi Republic', 'Kostroma Oblast', 'Krasnodar Krai', 'Krasnoyarsk Krai', 'Kurgan Oblast', 'Kursk Oblast', 'Leningrad Oblast', 'Lipetsk Oblast', 'Magadan Oblast', 'Mari El', 'Mordovia', 'Moscow', 'Moscow Oblast', 'Murmansk Oblast', 'Nenets Autonomous Okrug', 'Nizhny Novgorod Oblast', 'North Ossetia-Alania', 'Novgorod Oblast', 'Novosibirsk Oblast', 'Omsk Oblast', 'Orenburg Oblast', 'Oryol Oblast', 'Penza Oblast', 'Perm Krai', 'Primorsky Krai', 'Pskov Oblast', 'Rostov Oblast', 'Ryazan Oblast', 'Sakha Republic', 'Sakhalin Oblast', 'Samara Oblast', 'Saint Petersburg', 'Saratov Oblast', 'Smolensk Oblast', 'Stavropol Krai', 'Sverdlovsk Oblast', 'Tambov Oblast', 'Tatarstan', 'Tomsk Oblast', 'Tula Oblast', 'Tuva', 'Tver Oblast', 'Tyumen Oblast', 'Udmurtia', 'Ulyanovsk Oblast', 'Vladimir Oblast', 'Volgograd Oblast', 'Vologda Oblast', 'Voronezh Oblast', 'Yamalo-Nenets Autonomous Okrug', 'Yaroslavl Oblast', 'Zabaykalsky Krai'],
  'Saudi Arabia': ['Al Bahah', 'Al Jawf', 'Al Madinah', 'Al Qassim', 'Asir', 'Eastern Province', 'Hail', 'Jazan', 'Makkah', 'Najran', 'Northern Borders', 'Riyadh', 'Tabuk'],
  'Singapore': ['Central Region', 'East Region', 'North Region', 'North-East Region', 'West Region'],
  'South Korea': ['Busan', 'Chungcheongbuk-do', 'Chungcheongnam-do', 'Daegu', 'Daejeon', 'Gangwon-do', 'Gwangju', 'Gyeonggi-do', 'Gyeongsangbuk-do', 'Gyeongsangnam-do', 'Incheon', 'Jeju-do', 'Jeollabuk-do', 'Jeollanam-do', 'Sejong', 'Seoul', 'Ulsan'],
  'Spain': ['Andalusia', 'Aragon', 'Asturias', 'Balearic Islands', 'Basque Country', 'Canary Islands', 'Cantabria', 'Castile and León', 'Castilla-La Mancha', 'Catalonia', 'Ceuta', 'Extremadura', 'Galicia', 'La Rioja', 'Madrid', 'Melilla', 'Murcia', 'Navarre', 'Valencian Community'],
  'Sweden': ['Blekinge', 'Dalarna', 'Gävleborg', 'Gotland', 'Halland', 'Jämtland', 'Jönköping', 'Kalmar', 'Kronoberg', 'Norrbotten', 'Örebro', 'Östergötland', 'Skåne', 'Södermanland', 'Stockholm', 'Uppsala', 'Värmland', 'Västerbotten', 'Västernorrland', 'Västmanland', 'Västra Götaland'],
  'Switzerland': ['Aargau', 'Appenzell Ausserrhoden', 'Appenzell Innerrhoden', 'Basel-Landschaft', 'Basel-Stadt', 'Bern', 'Fribourg', 'Geneva', 'Glarus', 'Graubünden', 'Jura', 'Lucerne', 'Neuchâtel', 'Nidwalden', 'Obwalden', 'Schaffhausen', 'Schwyz', 'Solothurn', 'St. Gallen', 'Thurgau', 'Ticino', 'Uri', 'Valais', 'Vaud', 'Zug', 'Zürich'],
  'Taiwan': ['Changhua County', 'Chiayi City', 'Chiayi County', 'Hsinchu City', 'Hsinchu County', 'Hualien County', 'Kaohsiung', 'Keelung', 'Kinmen County', 'Lienchiang County', 'Miaoli County', 'Nantou County', 'New Taipei', 'Penghu County', 'Pingtung County', 'Taichung', 'Tainan', 'Taipei', 'Taitung County', 'Taoyuan', 'Yilan County', 'Yunlin County'],
  'Türkiye': ['Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Aksaray', 'Amasya', 'Ankara', 'Antalya', 'Ardahan', 'Artvin', 'Aydın', 'Balıkesir', 'Bartın', 'Batman', 'Bayburt', 'Bilecik', 'Bingöl', 'Bitlis', 'Bolu', 'Burdur', 'Bursa', 'Çanakkale', 'Çankırı', 'Çorum', 'Denizli', 'Diyarbakır', 'Düzce', 'Edirne', 'Elazığ', 'Erzincan', 'Erzurum', 'Eskişehir', 'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkâri', 'Hatay', 'Iğdır', 'Isparta', 'Istanbul', 'İzmir', 'Kahramanmaraş', 'Karabük', 'Karaman', 'Kars', 'Kastamonu', 'Kayseri', 'Kırıkkale', 'Kırklareli', 'Kırşehir', 'Kilis', 'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa', 'Mardin', 'Mersin', 'Muğla', 'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Osmaniye', 'Rize', 'Sakarya', 'Samsun', 'Şanlıurfa', 'Siirt', 'Sinop', 'Sivas', 'Şırnak', 'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Uşak', 'Van', 'Yalova', 'Yozgat', 'Zonguldak'],
  'United Arab Emirates': ['Abu Dhabi', 'Ajman', 'Dubai', 'Fujairah', 'Ras Al Khaimah', 'Sharjah', 'Umm Al Quwain'],
  'United Kingdom': ['England', 'Scotland', 'Wales', 'Northern Ireland', 'Guernsey', 'Isle of Man', 'Jersey'],
  'Vietnam': ['An Giang', 'Bà Rịa–Vũng Tàu', 'Bắc Giang', 'Bắc Kạn', 'Bạc Liêu', 'Bắc Ninh', 'Bến Tre', 'Bình Định', 'Bình Dương', 'Bình Phước', 'Bình Thuận', 'Cà Mau', 'Cần Thơ', 'Cao Bằng', 'Đà Nẵng', 'Đắk Lắk', 'Đắk Nông', 'Điện Biên', 'Đồng Nai', 'Đồng Tháp', 'Gia Lai', 'Hà Giang', 'Hà Nam', 'Hà Nội', 'Hà Tĩnh', 'Hải Dương', 'Hải Phòng', 'Hậu Giang', 'Hòa Bình', 'Hưng Yên', 'Khánh Hòa', 'Kiên Giang', 'Kon Tum', 'Lai Châu', 'Lâm Đồng', 'Lạng Sơn', 'Lào Cai', 'Long An', 'Nam Định', 'Nghệ An', 'Ninh Bình', 'Ninh Thuận', 'Phú Thọ', 'Phú Yên', 'Quảng Bình', 'Quảng Nam', 'Quảng Ngãi', 'Quảng Ninh', 'Quảng Trị', 'Sóc Trăng', 'Sơn La', 'Tây Ninh', 'Thái Bình', 'Thái Nguyên', 'Thanh Hóa', 'Thừa Thiên Huế', 'Tiền Giang', 'TP Hồ Chí Minh', 'Trà Vinh', 'Tuyên Quang', 'Vĩnh Long', 'Vĩnh Phúc', 'Yên Bái']
};

const US_STATE_CODE_TO_NAME = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', DC: 'District of Columbia', AS: 'American Samoa', GU: 'Guam', MP: 'Northern Mariana Islands', PR: 'Puerto Rico', VI: 'U.S. Virgin Islands', UM: 'U.S. Minor Outlying Islands', MH: 'Marshall Islands', FM: 'Micronesia', PW: 'Palau'
};

const US_STATE_CODES = new Set(Object.keys(US_STATE_CODE_TO_NAME));

function looksLikeUrlOrCoordinate(value) {
  const text = String(value || '').trim();
  return /^https?:\/\//i.test(text) || /^-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?$/.test(text);
}

function normalizeCountry(value, state) {
  const text = String(value || '').trim();
  if (text && !looksLikeUrlOrCoordinate(text) && COUNTRY_OPTIONS.includes(text)) return text;
  if (US_STATE_CODES.has(String(state || '').trim().toUpperCase())) return 'United States';
  return 'United States';
}

function normalizeRegion(value, country) {
  const text = String(value || '').trim();
  if (!text || looksLikeUrlOrCoordinate(text)) return 'Unknown';
  if (country === 'United States') {
    const code = text.toUpperCase();
    return US_STATE_CODE_TO_NAME[code] || text;
  }
  return text;
}

function uniqueSorted(values) {
  return [...new Set(values.filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), 'en'));
}


function flattenRecord(record) {
  const flat = {};
  Object.entries(record || {}).forEach(([section, value]) => {
    if (section === 'id' || section === 'status') return;
    if (value && typeof value === 'object') Object.assign(flat, value);
  });
  return flat;
}

function bySuffix(flat, suffix) {
  const found = Object.entries(flat).find(([key]) => key.endsWith(suffix));
  return found?.[1];
}

function numberOrNull(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function parseAddressTail(address) {
  const text = String(address || '');
  const m = text.match(/,\s*([^,]+),\s*([A-Z]{2})\s*(\d{5}(?:-\d{4})?)?\s*$/);
  return {
    city: m?.[1] || '',
    state: m?.[2] || '',
    postalCode: m?.[3] || '',
  };
}

function parsePolygon(value, lat, lng) {
  if (!value) return makeSmallPolygon(lat, lng);
  try {
    const geo = typeof value === 'string' ? JSON.parse(value) : value;
    const ring = geo?.coordinates?.[0];
    if (Array.isArray(ring)) return ring.map(([lon, la]) => [la, lon]);
  } catch (_) {
    return makeSmallPolygon(lat, lng);
  }
  return makeSmallPolygon(lat, lng);
}

function makeSmallPolygon(lat, lng) {
  if (!lat || !lng) return [];
  const d = 0.0012;
  return [[lat - d, lng - d], [lat - d, lng + d], [lat + d, lng + d], [lat + d, lng - d]];
}

function riskFromRecord(id, tokenType) {
  const base = (id * 13 + tokenType.length * 7) % 100;
  const score = Math.max(8, Math.min(96, base));
  if (score < 25) return { score, level: 'Low', color: '#1f9d55' };
  if (score < 50) return { score, level: 'Moderate', color: '#f59e0b' };
  if (score < 75) return { score, level: 'High', color: '#ef4444' };
  return { score, level: 'Critical', color: '#7c2d12' };
}

function normalizeSeedData(seedData) {
  const items = [];
  Object.entries(TOKEN_SOURCES).forEach(([sourceKey, meta]) => {
    const records = seedData?.[sourceKey];
    if (!Array.isArray(records)) return;
    records.forEach((record) => {
      const flat = flattenRecord(record);
      const id = Number(record.id || items.length + 1);
      const tokenCode = `${meta.prefix}${String(id).padStart(3, '0')}`;
      const lat = numberOrNull(bySuffix(flat, '_8_6')) ?? numberOrNull(bySuffix(flat, '_2_5'));
      const lng = numberOrNull(bySuffix(flat, '_8_7')) ?? numberOrNull(bySuffix(flat, '_2_6'));
      if (!lat || !lng) return;
      const tokenTypeRaw = bySuffix(flat, '_1_2') || meta.label;
      const title = bySuffix(flat, '_1_3') || `${meta.label} Token ${id}`;
const ASSET_CLASS_SUFFIX_BY_SOURCE = {
  RENT_ASSET_PROFILE: '_1_4',
  REVSHARE_ASSET_PROFILE: '_1_4',
  SALEPROFIT_ASSET_PROFILE: '_1_4',
  EQAPP_ASSET_PROFILE: '_1_4',
  PREFEQ_ASSET_PROFILE: '_1_4',
  COMEQ_ASSET_PROFILE: '_1_4',

  DEBT_ASSET_PROFILE: '_1_4',
  HYBRID_ASSET_PROFILE: '_1_4',
  PREAUCT_AUCTION_OPPORTUNITY: '_1_3',
  POSTAUCT_AUCTION_RESULT_INTAKE: '_1_3'
};
const assetClassSuffix = ASSET_CLASS_SUFFIX_BY_SOURCE[sourceKey];

const assetClass =
  bySuffix(flat, assetClassSuffix) || 'Real Asset';

      const sector = bySuffix(flat, '_1_5') || 'Mixed Use';
      const offeringStatus = bySuffix(flat, '_1_6') || bySuffix(flat, '_4_0') || record.status || 'Active';
      const address = bySuffix(flat, '_8_5') || bySuffix(flat, '_2_0') || 'Address pending';
      const parsedAddress = parseAddressTail(address);
      const city = bySuffix(flat, '_2_1') || parsedAddress.city || 'Unknown';
      const county = bySuffix(flat, '_2_2') || 'Unknown';
      const rawState = bySuffix(flat, '_2_3') || parsedAddress.state || 'Unknown';
      const country = normalizeCountry(bySuffix(flat, '_8_4') || bySuffix(flat, '_2_7'), rawState);
      const state = normalizeRegion(rawState, country);
      const postalCode = bySuffix(flat, '_2_4') || parsedAddress.postalCode || '';
      const publicVisibility = bySuffix(flat, '_8_1') || 'Public';
      const investmentStatus = bySuffix(flat, '_8_2') || 'Listed';
      const boundaryType = bySuffix(flat, '_8_9') || 'Point Only';
      const mapZoom = Math.max(17, numberOrNull(bySuffix(flat, '_8_8')) || 17);
      const polygon = parsePolygon(bySuffix(flat, '_8_13'), lat, lng);
      const risk = riskFromRecord(id, meta.label);
      const minimumInvestment = 500 + (id % 12) * 500;
      const targetYield = Number((5 + (id % 9) * 0.7).toFixed(1));
      const fundedPercent = Math.min(99, 10 + ((id * 11) % 85));
      items.push({
        id: `${sourceKey}-${id}`,
        sourceKey,
        tokenCode,
        tokenType: meta.label,
        tokenGroup: meta.group,
        tokenTypeRaw,
        title,
        assetClass,
        sector,
        offeringStatus,
        address,
        city,
        county,
        state,
        country,
        postalCode,
        lat,
        lng,
        publicVisibility,
        investmentStatus,
        boundaryType,
        mapZoom,
        polygon,
        riskScore: risk.score,
        riskLevel: risk.level,
        riskColor: risk.color,
        minimumInvestment,
        targetYield,
        fundedPercent,
        reviewSummaryCode: `RV-${meta.prefix}${String(id).padStart(3, '0')}`,
      });
    });
  });
  return items;
}

function ResizeMapOnLayoutChange({ selectedId, viewMode }) {
  const map = useMap();
  useEffect(() => {
    setTimeout(() => map.invalidateSize(), 120);
  }, [map, selectedId, viewMode]);
  return null;
}


function FlyToSelected({ selected }) {
  const map = useMap();
  useEffect(() => {
    if (!selected) return;
    const targetZoom = Math.max(17, selected.mapZoom || 17);
    map.flyTo([selected.lat, selected.lng], targetZoom, { duration: 0.65 });
  }, [map, selected]);
  return null;
}

function ZoomWatcher({ onZoom }) {
  useMapEvents({ zoomend: (e) => onZoom(e.target.getZoom()) });
  return null;
}

function MarkerIcon({ item, selected }) {
  return L.divIcon({
    className: 'rwae-marker-shell',
    html: `<div class="rwae-marker ${selected ? 'selected' : ''}" style="--risk:${item.riskColor}"><span>${item.tokenCode}</span></div>`,
    iconSize: [56, 34],
    iconAnchor: [28, 34],
    popupAnchor: [0, -32],
  });
}

function TokenCard({ item, selected, onSelect, reviewCount }) {
	  const { t } = useTranslation();
  return (
    <button className={`token-card ${selected ? 'is-selected' : ''}`} onClick={() => onSelect(item)}>
      <div className="card-image" aria-hidden="true">
        <div className="card-map-thumb">{item.tokenCode}</div>
        <span className="risk-pill" style={{ background: item.riskColor }}> {t(item.riskLevel)}</span>
      </div>
      <div className="card-body">
        <div className="card-topline">
          <span className="token-code">{item.tokenCode}</span>
          <span>{t(item.tokenType)}</span>
        </div>
        <h3>{item.title}</h3>
        <p>{item.address}</p>
<div className="tag-row">
  <span>{t(item.assetClass)}</span>
  <span>{t(item.sector)}</span>
  <span>{reviewCount} {t('review')}</span>
</div>
      </div>
    </button>
  );
}


function getTokenActions(item) {
  const baseActions = ['Request purchase', 'Reserve allocation', 'Open data room'];
  if (!item) return baseActions;

  if (item.tokenGroup === 'Auction') {
    return item.tokenType === 'Pre-Auction Pool'
      ? ['Join auction pool', 'Submit bid-interest request', 'Open auction package', 'Schedule investor call']
      : ['Request post-auction allocation', 'Review foreclosure package', 'Open data room', 'Schedule investor call'];
  }

  if (item.tokenGroup === 'Debt') return ['Request note purchase', 'View note terms', 'Open data room', 'Schedule lender call'];
  if (item.tokenGroup === 'Equity') return ['Buy equity token', 'Request subscription', 'View offering memorandum', 'Open data room'];
  if (item.tokenGroup === 'Income') return ['Buy income token', 'Request subscription', 'View cash-flow summary', 'Open data room'];
  if (item.tokenGroup === 'Exit') return ['Request sale-profit token', 'View exit plan', 'Open data room', 'Schedule investor call'];
  if (item.tokenGroup === 'Hybrid') return ['Request hybrid token', 'Compare components', 'Open data room', 'Schedule investor call'];

  return baseActions;
}

function DetailPanel({ item, reviews, onClose, onAddReview }) {
  const { t } = useTranslation();

  const [text, setText] = useState('');
  const [rating, setRating] = useState(5);
  if (!item) return null;
  const itemReviews = reviews.filter((r) => r.tokenId === item.id);
  const actionButtons = getTokenActions(item);
  const submit = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    onAddReview({ tokenId: item.id, code: `${item.reviewSummaryCode}-${String(itemReviews.length + 1).padStart(2, '0')}`, rating, text: text.trim(), createdAt: new Date().toISOString() });
    setText('');
    setRating(5);
  };
  return (
    <aside className="detail-panel" aria-label="Token details">
      <div className="detail-header">
        <div><span className="token-code large">{item.tokenCode}</span><h2>{t(item.title)}</h2></div>
        <button onClick={onClose} aria-label="Close details">×</button>
      </div>
      <div className="detail-grid">
<span>{t('Type')}</span>
<b>{t(item.tokenType)}</b>

<span>{t('Risk')}</span>
<b style={{ color: item.riskColor }}>
  {t(item.riskLevel)} / {item.riskScore}
</b>

<span>{t('Status')}</span>
<b>{t(item.investmentStatus)}</b>

<span>{t('Boundary')}</span>
<b>{t(item.boundaryType)}</b>

<span>{t('Location')}</span>
<b>{item.city}, {t(item.state)}</b>

<span>{t('Min Investment')}</span>
<b>${item.minimumInvestment.toLocaleString()}</b>
      </div>
	  
	  
<div className="progress-block">
  <span>{t('Funding progress')}</span>
  <div>
    <i style={{ width: `${item.fundedPercent}%` }} />
  </div>
  <b>{item.fundedPercent}%</b>
</div>      <section className="token-actions" aria-label="Token purchase actions">
       <h3>{t('Purchase / request actions')}</h3>
        <div>
{actionButtons.map((label) => (
  <button key={label} type="button">
    {t(label)}
  </button>
))}
        </div>
      </section>
      <h3>{t('Customer reviews on map/list')}</h3>
      <div className="review-list">
        {itemReviews.length === 0 ? <p className="muted">{t('No public review yet.')}</p> : itemReviews.map((r) => <div key={r.code} className="review-item"><b>{r.code} · {r.rating}/5</b><p>{r.text}</p></div>)}
      </div>
      <form className="review-form" onSubmit={submit}>
        <label>{t('Review rating')}
          <select value={rating} onChange={(e) => setRating(Number(e.target.value))}>{[5,4,3,2,1].map((n) => <option key={n} value={n}>{n}</option>)}</select>
        </label>
        <label>{t('Review text')}
          <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t('Example: RV text for map/list display...')} />
        </label>
        <button type="submit">{t('Add review')}</button>
      </form>
    </aside>
  );
}

export default function StarkRwaeMarketplace({ seedData, apiItems }) {
  const [query, setQuery] = useState('');
  const [viewMode, setViewMode] = useState('split');
  const [baseMap, setBaseMap] = useState('map');
  const [showLayers, setShowLayers] = useState(false);
  const [showBoundaries, setShowBoundaries] = useState(true);
  const [showAllBoundaries, setShowAllBoundaries] = useState(false);
  const [showRisk, setShowRisk] = useState(true);
  const [showReviews, setShowReviews] = useState(true);
  const [zoom, setZoom] = useState(13);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ type: 'All', risk: 'All', status: 'All', asset: 'All', country: 'All', state: 'All', investable: false });
  const [reviews, setReviews] = useState([
    { tokenId: 'RENT_ASSET_PROFILE-1', code: 'RV-RI001-01', rating: 5, text: 'Good location visibility and clean data room.', createdAt: '2026-06-24' },
    { tokenId: 'DEBT_ASSET_PROFILE-4', code: 'RV-DN004-01', rating: 4, text: 'Debt structure is easy to understand.', createdAt: '2026-06-24' },
  ]);

  const allItems = useMemo(() => apiItems?.length ? apiItems : normalizeSeedData(seedData), [apiItems, seedData]);
  const options = useMemo(() => {
    const countryRegions = filters.country !== 'All' ? COUNTRY_REGION_OPTIONS[filters.country] : null;
    return {
      type: ['All', ...uniqueSorted(allItems.map((x) => x.tokenType))],
      risk: ['All', 'Low', 'Moderate', 'High', 'Critical'],
      status: ['All', ...uniqueSorted(allItems.map((x) => x.investmentStatus))],
      asset: ['All', ...uniqueSorted(allItems.map((x) => x.assetClass))],
      country: COUNTRY_OPTIONS,
      state: ['All', ...(countryRegions ? uniqueSorted(countryRegions) : uniqueSorted(allItems.map((x) => x.state)))],
    };
  }, [allItems, filters.country]);
    const { t, i18n } = useTranslation();

  useEffect(() => {
    setFilters((current) => {
      if (current.state === 'All') return current;
      const allowedStates = current.country === 'All'
        ? uniqueSorted(allItems.map((x) => x.state))
        : uniqueSorted(COUNTRY_REGION_OPTIONS[current.country] || []);
      return allowedStates.includes(current.state) ? current : { ...current, state: 'All' };
    });
  }, [filters.country, allItems]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allItems.filter((item) => {
      if (filters.type !== 'All' && item.tokenType !== filters.type) return false;
      if (filters.risk !== 'All' && item.riskLevel !== filters.risk) return false;
      if (filters.status !== 'All' && item.investmentStatus !== filters.status) return false;
      if (filters.asset !== 'All' && item.assetClass !== filters.asset) return false;
      if (filters.country !== 'All' && (item.country || 'United States') !== filters.country) return false;
      if (filters.state !== 'All' && item.state !== filters.state) return false;
      if (filters.investable && !/investable|active|listed/i.test(item.investmentStatus)) return false;
      if (!q) return true;
      return [item.tokenCode, item.title, item.address, item.city, item.county, item.state, item.country, item.tokenType, item.assetClass, item.sector].join(' ').toLowerCase().includes(q);
    });
  }, [allItems, filters, query]);

  const center = selected ? [selected.lat, selected.lng] : (filtered[0] ? [filtered[0].lat, filtered[0].lng] : DEFAULT_CENTER);
  const reviewCount = (tokenId) => reviews.filter((r) => r.tokenId === tokenId).length;
  const boundaryItems = useMemo(() => {
    if (!showBoundaries || zoom < 15) return [];
    if (selected) return [selected];
    if (showAllBoundaries && filtered.length <= 50) return filtered;
    return [];
  }, [showBoundaries, showAllBoundaries, zoom, selected, filtered]);

  return (
    <main className={`rwae-page mode-${viewMode}`}   dir={i18n.dir()}>
<section className="search-strip">
  <div className="big-search">
    <span>⌕</span>
    <input
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      placeholder={t('Search: "RI income Austin", "low risk land", "auction pool"')}
    />
  </div>

  <button className="save-btn">
    {t('♡ Save search')}
  </button>

  <div className="view-toggle">
    <button
      onClick={() => setViewMode('list')}
      className={viewMode === 'list' ? 'active' : ''}
    >
      {t('List')}
    </button>

    <button
      onClick={() => setViewMode('split')}
      className={viewMode !== 'list' ? 'active' : ''}
    >
      {t('Map')}
    </button>
  </div>
</section>

<section className="filter-strip">
  <button className="filter-main">
    {t('☰ Filters')}
  </button>

  {Object.entries({
    type: 'Token type',
    risk: 'Risk color',
    status: 'Offering status',
    asset: 'Asset class',
    country: 'Country',
    state: 'State'
  }).map(([key, label]) => (
    <label key={key}>
      {t(label)}

      <select
        value={filters[key]}
        onChange={(e) =>
          setFilters((f) =>
            key === 'country'
              ? { ...f, country: e.target.value, state: 'All' }
              : { ...f, [key]: e.target.value }
          )
        }
      >
        {options[key].map((x) => (
          <option key={x} value={x}>
            {t(x)}
          </option>
        ))}
      </select>
    </label>
  ))}

  <label className="check-filter">
    <input
      type="checkbox"
      checked={filters.investable}
      onChange={(e) =>
        setFilters((f) => ({
          ...f,
          investable: e.target.checked
        }))
      }
    />
    {t('Investable only')}
  </label>
</section>

      <section className="result-layout">
        <section className="list-pane">
<div className="result-heading">
  <h1>
    {t('Tokenized land & real estate offerings')}
  </h1>

  <p>
    {filtered.length.toLocaleString()} {t('tokens · Sort by relevant listings')}
  </p>
</div>          <div className="cards-grid">
            {filtered.map((item) => <TokenCard key={item.id} item={item} selected={selected?.id === item.id} onSelect={setSelected} reviewCount={reviewCount(item.id)} />)}
          </div>
        </section>

        {viewMode !== 'list' && (
          <section className="map-pane">
<div className="map-toolbar">
  <button onClick={() => setShowLayers((v) => !v)}>
    {t('▣ Layers')}
  </button>

  <button
    onClick={() =>
      setBaseMap(baseMap === 'map' ? 'satellite' : 'map')
    }
  >
    {baseMap === 'map' ? t('Satellite') : t('Map')}
  </button>

  <span>
    {t('Showing')} {filtered.length} {t('tokens')}
  </span>
</div>
{showLayers && (
  <div className="layer-panel">
    <b>{t('Map layers')}</b>

    <p>
      {t('Use these options to show or hide map-only overlays.')}
    </p>

    <label>
      <input
        type="checkbox"
        checked={showBoundaries}
        onChange={(e) => setShowBoundaries(e.target.checked)}
      />
      {t('Token boundary polygons after zoom 15+')}
    </label>

    <label>
      <input
        type="checkbox"
        checked={showAllBoundaries}
        onChange={(e) => setShowAllBoundaries(e.target.checked)}
      />
      {t('Show all visible boundaries only when 50 or fewer tokens are filtered')}
    </label>

    <label>
      <input
        type="checkbox"
        checked={showRisk}
        onChange={(e) => setShowRisk(e.target.checked)}
      />
      {t('Risk color pins')}
    </label>

    <label>
      <input
        type="checkbox"
        checked={showReviews}
        onChange={(e) => setShowReviews(e.target.checked)}
      />
      {t('Customer review code layer')}
    </label>
  </div>
)}			
            <MapContainer center={center} zoom={selected?.mapZoom || 5} minZoom={4} maxZoom={20} maxBounds={WORLD_BOUNDS} maxBoundsViscosity={1.0} zoomControl={false} className="rwae-map">
              <ResizeMapOnLayoutChange selectedId={selected?.id} viewMode={viewMode} />
              <FlyToSelected selected={selected} />
              <ZoomWatcher onZoom={setZoom} />
              <ZoomControl position="bottomright" />
              {baseMap === 'map' ? <TileLayer noWrap bounds={WORLD_BOUNDS} attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" /> : <TileLayer noWrap bounds={WORLD_BOUNDS} attribution='Tiles &copy; Esri' url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />}
              {filtered.map((item) => (
                <Marker key={item.id} position={[item.lat, item.lng]} icon={MarkerIcon({ item: showRisk ? item : { ...item, riskColor: '#111827' }, selected: selected?.id === item.id })} eventHandlers={{ click: () => setSelected(item) }}>
<Popup>
  <b>
    {item.tokenCode} · {t(item.tokenType)}
  </b>

  <br />

  {item.title}

  <br />

  {t('Risk')}: {t(item.riskLevel)} / {item.riskScore}

  <br />

  {showReviews &&
    `${item.reviewSummaryCode}: ${reviewCount(item.id)} ${t('review(s)')}`}
</Popup>
                </Marker>
              ))}
              {boundaryItems.map((item) => item.polygon?.length ? <Polygon key={`${item.id}-poly`} positions={item.polygon} pathOptions={{ color: item.riskColor, weight: selected?.id === item.id ? 4 : 2, fillOpacity: selected?.id === item.id ? 0.22 : 0.08 }} eventHandlers={{ click: () => setSelected(item) }} /> : null)}
            </MapContainer>
          </section>
        )}
      </section>

      <DetailPanel item={selected} reviews={reviews} onClose={() => setSelected(null)} onAddReview={(r) => setReviews((list) => [r, ...list])} />
    </main>
  );
}

export { normalizeSeedData };
